import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";

const VIDEO_URL =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_goa-invest/artifacts/fn2gr3oc_video-output-FCE6AE29-5200-4AC3-A36A-390CEA2989DD-1.MP4";
const POSTER_URL = "/media/video-poster.jpg";

export const VideoShowcase = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    const next = !muted;
    el.muted = next;
    setMuted(next);
    if (!next) {
      // Ensure playback resumes when user unmutes (autoplay only works muted)
      el.play().catch(() => {});
    }
  };

  return (
    <section
      id="video-showcase"
      data-testid="video-showcase-section"
      className="relative bg-[#02060D] py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10"
        >
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-[#D1B677] font-medium">
              Goa · In Motion
            </span>
            <h2 className="mt-4 font-serif text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] max-w-3xl">
              A coastline built for{" "}
              <span className="italic text-[#D1B677]">global opportunity.</span>
            </h2>
          </div>
          <p className="text-[#A1AEBF] text-base leading-relaxed max-w-md">
            The energy, elegance and enterprise of Goa — captured in a single frame.
            Tap the speaker for sound.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="relative overflow-hidden border border-[#D1B677]/20 shadow-[0_30px_100px_-30px_rgba(209,182,119,0.25)]"
        >
          <video
            ref={videoRef}
            data-testid="showcase-video"
            src={VIDEO_URL}
            poster={POSTER_URL}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="w-full h-auto max-h-[85vh] object-cover bg-[#02060D] block cursor-pointer"
            onClick={toggleMute}
          />

          {/* Subtle inner vignette */}
          <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_120px_rgba(2,6,13,0.6)]" />

          {/* Sound toggle button */}
          <button
            type="button"
            onClick={toggleMute}
            aria-label={muted ? "Unmute video" : "Mute video"}
            data-testid="video-sound-toggle"
            className="group absolute bottom-5 right-5 md:bottom-7 md:right-7 flex items-center gap-2 h-11 pl-3 pr-4 border border-[#D1B677]/60 bg-[#02060D]/70 backdrop-blur-md text-[#D1B677] hover:bg-[#D1B677] hover:text-[#02060D] transition-all duration-300"
          >
            {muted ? (
              <SpeakerSlash size={18} weight="light" />
            ) : (
              <SpeakerHigh size={18} weight="fill" />
            )}
            <span className="text-[11px] tracking-[0.25em] uppercase font-medium">
              {muted ? "Sound Off" : "Sound On"}
            </span>
          </button>

          {/* Play/pause hint that appears while muted */}
          {muted && (
            <div className="pointer-events-none absolute top-5 left-5 md:top-7 md:left-7 flex items-center gap-2 border border-white/15 bg-[#02060D]/50 backdrop-blur-md px-3 py-2">
              <span className="w-1.5 h-1.5 bg-[#D1B677] rounded-full animate-pulse" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-white/80">
                Tap for Sound
              </span>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default VideoShowcase;
